import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowRight, Sparkles, Loader2, Mail, ChevronLeft, ChevronRight
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import NewsletterModal from '@/components/NewsletterModal';
import { fetchBlogPosts, type BlogPost } from '@/lib/cms';
import BreadcrumbJsonLd from '@/components/BreadcrumbJsonLd';

const GRADIENT_PALETTES = [
  'from-[#635bff] to-[#00d4ff]',
  'from-[#00d4ff] to-[#00ff88]',
  'from-[#a855f7] to-[#ec4899]',
  'from-[#ff6b6b] to-[#ffa500]',
  'from-[#635bff] to-[#a855f7]',
  'from-[#00d4ff] to-[#635bff]',
];

function getPostGradient(index: number): string {
  return GRADIENT_PALETTES[index % GRADIENT_PALETTES.length];
}

function estimateReadTime(content: string): string {
  const text = content.replace(/<[^>]+>/g, '');
  const words = text.split(/\s+/).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min read`;
}

function formatDate(dateString: string): string {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const FEATURED_COUNT = 3;
const POSTS_PER_PAGE = 12; // 3 featured + 9 grid on page 1; 12 grid on subsequent pages

function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  if (totalPages <= 1) return null;

  // Build visible page numbers: always show first, last, current ±1, with ellipsis
  function getPageNumbers(): (number | '…')[] {
    const pages: (number | '…')[] = [];
    const delta = 1;
    const range: number[] = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (range[0] > 2) pages.push(1, '…');
    else pages.push(1);

    pages.push(...range);

    if (range[range.length - 1] < totalPages - 1) pages.push('…', totalPages);
    else pages.push(totalPages);

    return pages;
  }

  const pageNums = getPageNumbers();

  return (
    <nav aria-label="Blog pagination" className="mt-12 flex items-center justify-center gap-1">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Previous page"
        className="p-2 rounded-lg text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-30 disabled:pointer-events-none transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      {pageNums.map((p, idx) =>
        p === '…' ? (
          <span key={`ellipsis-${idx}`} className="px-2 text-slate-400 select-none">…</span>
        ) : (
          <button
            key={p}
            onClick={() => onPageChange(p as number)}
            aria-label={`Page ${p}`}
            aria-current={p === currentPage ? 'page' : undefined}
            className={`min-w-[2.25rem] h-9 px-2 rounded-lg text-sm font-medium transition-colors
              ${p === currentPage
                ? 'bg-[#635bff] text-white'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10'
              }`}
          >
            {p}
          </button>
        )
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Next page"
        className="p-2 rounded-lg text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-30 disabled:pointer-events-none transition-colors"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </nav>
  );
}

const BlogPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [blogLoading, setBlogLoading] = useState(true);
  const [blogError, setBlogError] = useState<string | null>(null);
  const [newsletterOpen, setNewsletterOpen] = useState(false);

  const currentPage = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10));

  useEffect(() => {
    let cancelled = false;

    async function loadBlogPosts() {
      try {
        setBlogLoading(true);
        setBlogError(null);
        const { posts } = await fetchBlogPosts(200);
        if (cancelled) return;
        setBlogPosts(posts);
      } catch (err) {
        if (cancelled) return;
        setBlogError(err instanceof Error ? err.message : 'Failed to load blog posts');
      } finally {
        if (!cancelled) setBlogLoading(false);
      }
    }

    loadBlogPosts();
    return () => { cancelled = true; };
  }, []);

  const totalPages = blogPosts.length > 0 ? Math.ceil(blogPosts.length / POSTS_PER_PAGE) : 1;
  const safeCurrentPage = Math.min(currentPage, totalPages);

  const startIdx = (safeCurrentPage - 1) * POSTS_PER_PAGE;
  const pagePosts = blogPosts.slice(startIdx, startIdx + POSTS_PER_PAGE);
  const featuredPosts = safeCurrentPage === 1 ? pagePosts.slice(0, FEATURED_COUNT) : [];
  const gridPosts = safeCurrentPage === 1 ? pagePosts.slice(FEATURED_COUNT) : pagePosts;

  function handlePageChange(page: number) {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', String(page));
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0f]">
      <BreadcrumbJsonLd crumbs={[
        { name: 'Home', item: '/' },
        { name: 'Blog' },
      ]} />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#635bff]/20 via-transparent to-[#00d4ff]/10" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#635bff]/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-[#00d4ff]/15 rounded-full blur-3xl animate-float-delayed" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-4xl">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-[#635bff] bg-[#635bff]/10 border border-[#635bff]/20 rounded-full mb-6">
              <Sparkles className="w-4 h-4" />
              Blog
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white leading-tight">
              Insights & Updates
            </h1>
            <p className="mt-6 text-xl text-slate-600 dark:text-slate-400">
              The latest on interactive advertising, industry trends, and tips to help you drive better results.
            </p>
            <button
              onClick={() => setNewsletterOpen(true)}
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-[#635bff] text-white text-sm font-semibold rounded-lg hover:bg-[#5449e6] transition-colors"
            >
              <Mail className="w-4 h-4" />
              Subscribe to our blog
            </button>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {blogLoading ? (
            <div className="flex items-center justify-center py-32">
              <Loader2 className="w-8 h-8 text-[#635bff] animate-spin" />
              <span className="ml-3 text-slate-600 dark:text-slate-400">Loading blog posts...</span>
            </div>
          ) : blogError ? (
            <div className="text-center py-32">
              <p className="text-red-500 mb-4">{blogError}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-[#635bff] text-white rounded-lg hover:bg-[#635bff]/90 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : (
          <>
            {/* Featured Posts — page 1 only */}
            {featuredPosts.length > 0 && (
              <div className="grid md:grid-cols-3 gap-6 mb-12">
                {featuredPosts.map((post, idx) => (
                  <Link
                    to={`/blog/${post.slug}`}
                    key={post.id}
                    className="group cursor-pointer bg-slate-50 dark:bg-white/5 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden hover:bg-slate-100 dark:hover:bg-white/10 hover:border-[#635bff]/30 transition-all"
                  >
                    <div className="aspect-[16/9] relative overflow-hidden">
                      {post.featured_image ? (
                        <img
                          src={post.featured_image}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className={`w-full h-full bg-gradient-to-br ${getPostGradient(idx)} flex items-center justify-center p-6`}>
                          <span className="text-white/90 text-lg font-semibold text-center line-clamp-3">{post.title}</span>
                        </div>
                      )}
                      <div className="absolute top-4 left-4">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-white bg-[#635bff] rounded-full">
                          Featured
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-slate-900 dark:text-white group-hover:text-[#635bff] line-clamp-2 transition-colors">
                        {post.title}
                      </h3>
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                        {post.excerpt.replace(/&hellip;/g, '...').replace(/<[^>]+>/g, '').slice(0, 160)}
                      </p>
                      <div className="mt-4 flex items-center justify-between">
                        <p className="text-xs text-slate-500">
                          {formatDate(post.publish_date)}{post.content ? ` | ${estimateReadTime(post.content)}` : ''}
                        </p>
                        <ArrowRight className="w-5 h-5 text-[#635bff] group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Blog Grid — 4 columns */}
            {gridPosts.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {gridPosts.map((post, idx) => (
                  <Link
                    to={`/blog/${post.slug}`}
                    key={post.id}
                    className="group cursor-pointer bg-slate-50 dark:bg-white/5 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden hover:bg-slate-100 dark:hover:bg-white/10 hover:border-[#635bff]/30 transition-all"
                  >
                    <div className="aspect-[16/9] relative overflow-hidden">
                      {post.featured_image ? (
                        <img
                          src={post.featured_image}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className={`w-full h-full bg-gradient-to-br ${getPostGradient(idx + (safeCurrentPage === 1 ? FEATURED_COUNT : 0))} flex items-center justify-center p-4`}>
                          <span className="text-white/90 text-sm font-semibold text-center line-clamp-3">{post.title}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-white group-hover:text-[#635bff] line-clamp-2 transition-colors">
                        {post.title}
                      </h3>
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                        {post.excerpt.replace(/&hellip;/g, '...').replace(/<[^>]+>/g, '').slice(0, 120)}
                      </p>
                      <p className="mt-3 text-xs text-slate-500">
                        {formatDate(post.publish_date)}{post.content ? ` | ${estimateReadTime(post.content)}` : ''}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            <Pagination
              currentPage={safeCurrentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
          )}
        </div>
      </section>

      <Footer />
      {newsletterOpen && <NewsletterModal onClose={() => setNewsletterOpen(false)} />}
    </div>
  );
};

export default BlogPage;
